import React from "react";

/**
 * ── Model-authored link policy (REF-6217 P1-5) ──────────────────────────────────────────────
 *
 * Ask Refabric has exactly one navigation authority: the Capability Graph. The model emits
 * `NAV:<capability_id>`, the backend resolves it against the generated graph, and the FE renders
 * that as the single CTA button. A wrong label or a dead in-app link is impossible there, because
 * an id the graph does not know is dropped backend-side (`CapabilityGraph.resolve_action`).
 *
 * Model PROSE is rendered as markdown, and markdown has links. So prose can mint an `<a href>`
 * the graph never approved — a second, unvalidated navigation authority sitting next to the first
 * one. `DashboardChatbot.test.tsx` already asserted the absence of one ("the route comes from
 * graph metadata, never from model prose, so there is no external href anywhere in this path")
 * with nothing enforcing it. This module is the enforcement.
 *
 * What the rendering stack already gives us, and why it is not enough:
 *
 *   - `rehype-raw` is NOT installed, so a raw `<a>` in the model's output is never rendered. That
 *     closes the raw-HTML route, and adding `rehype-raw` would reopen it.
 *   - react-markdown 8's default `uriTransformer` (applied because we pass no `transformLinkUri`)
 *     rewrites an unknown scheme to `javascript:void(0)` — inert, but still an href, and the rule
 *     it enforces is about SCHEMES only.
 *   - It returns anything starting with `/` or `#` UNCHANGED. `[Colors](/colors)` therefore
 *     renders as a live in-app link. That is the actual hole: not a script URL, an ordinary route
 *     the graph never authorised.
 *   - The backend's `scrub_public_urls` fails closed on refabric.com-SHAPED absolute URLs, but its
 *     regex (`_REFABRIC_URL_RE`) requires a host containing "refabric", so a bare `/colors` is not
 *     matched there either.
 *
 * The policy is therefore an ALLOWLIST, not a blocklist: an href becomes a link only when it is an
 * absolute `http`/`https` URL pointing somewhere that is not this app, or a `mailto:` / `tel:`
 * contact URL. Everything else keeps its link TEXT and loses its href, so the sentence still reads
 * correctly and nothing is silently deleted.
 *
 * Absolute links to our own public marketing site keep working: it is a different host from the
 * app in every environment, and the backend has already validated them against its canonical
 * allowlist before they reach here.
 */

/** Schemes a model-authored link may use. `mailto:`/`tel:` are contact affordances, not navigation. */
const ALLOWED_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

/**
 * C0 controls and space. Browsers strip tab / newline / CR while resolving a URL, so
 * `java<TAB>script:alert(1)` is a live scheme to the browser; a check that did not strip them
 * first would read the scheme as `java\tscript:` and wave it through as "not a known scheme".
 */
// The `no-control-regex` disable is deliberate and load-bearing: the control characters ARE the
// subject. A checker that could not see them would read `java<TAB>script:` as an unknown scheme and
// let a live `javascript:` URL through.
// eslint-disable-next-line no-control-regex
const IGNORED_IN_URL = /[\u0000-\u0020\u007f]/g;

/** Trailing dots are DNS-equivalent, and `new URL` keeps them while `location.host` never has one. */
const stripTrailingDots = (hostname: string): string => hostname.toLowerCase().replace(/\.+$/, "");

/**
 * The registrable domain every Refabric surface lives under — the marketing site AND every app
 * environment (dev, stage, preprod, production).
 */
const REFABRIC_DOMAIN = "refabric.com";

/**
 * The only Refabric hosts model prose may link to: the public marketing site.
 *
 * An allowlist rather than a "not an app host" test, and that distinction is the whole finding.
 * Reviewing this, Codex produced `https://stage.%72efabric.com/canvas-projects`: the backend's
 * scrubber requires the literal ASCII word "refabric" in the raw text, so percent-encoding walks
 * straight past it — and then `new URL` canonicalises the host back to `stage.refabric.com`, a real
 * Refabric app. Soft-hyphen and fullwidth spellings do the same. Checking only the CURRENT host let
 * every sibling environment through.
 *
 * So this layer no longer trusts the backend for anything under `refabric.com`. It decides on the
 * CANONICALISED hostname, which is what the browser will actually resolve, and admits only these.
 *
 * ⚠️ FAIL-CLOSED BY DESIGN: a new legitimate marketing subdomain must be added here before prose can
 * link to it. That is the intended trade — a missing link is cosmetic, an unauthorised in-app
 * destination is not.
 */
const PUBLIC_REFABRIC_HOSTS = new Set([REFABRIC_DOMAIN, `www.${REFABRIC_DOMAIN}`]);

/** Is this hostname the Refabric registrable domain, or anything beneath it? */
const isRefabricHost = (hostname: string): boolean => hostname === REFABRIC_DOMAIN || hostname.endsWith(`.${REFABRIC_DOMAIN}`);

/**
 * Is this URL pointing at the app the reader is already looking at?
 *
 * Compared per component, because `location.host` and `URL.host` do not normalise the same way.
 * `https://dev.refabric.com./colors` parses to host `dev.refabric.com.` — unequal as a string, the
 * same name to DNS. The port is compared separately so a trailing dot cannot hide behind `:3000`,
 * and the synthetic URL is built with the LINK's protocol so an explicit `:443` on an
 * `http://` link is not silently erased by a hard-coded `https:`.
 *
 * FAILS CLOSED: an `appHost` this cannot parse is treated as a match, so a caller that passes
 * something unexpected loses links rather than gaining an unchecked in-app destination.
 */
const isAppHost = (url: URL, appHost: string): boolean => {
  let app: URL;
  try {
    app = new URL(`${url.protocol}//${appHost}`);
  } catch {
    return true;
  }

  return stripTrailingDots(url.hostname) === stripTrailingDots(app.hostname) && url.port === app.port;
};

/**
 * The href to render for a model-authored link, or `null` to render its text with no link at all.
 *
 * Returns the NORMALISED href rather than the original: the decision is made on the stripped
 * string, so rendering the original would let a control character produce a link the check never
 * actually approved.
 *
 * `appHost` is the app's own `location.host` (host, so the port counts — `localhost:3000` and
 * `localhost:4000` are different origins). Passed in rather than read here so this stays pure, and
 * so a test states the host it means instead of inheriting jsdom's.
 */
export const safeChatbotLinkHref = (rawHref: string | null | undefined, appHost: string): string | null => {
  if (!rawHref) return null;

  const href = rawHref.replace(IGNORED_IN_URL, "");
  if (!href) return null;

  // Root-relative (`/colors`), protocol-relative (`//host/x`), query-only (`?tab=x`) and hash
  // (`#section`) hrefs all resolve against the CURRENT origin — i.e. they are in-app navigation,
  // which the Capability Graph owns. `//host` is rejected alongside them on purpose: it reads as a
  // path but resolves to a foreign host, and no honest answer needs that form.
  if (href.startsWith("/") || href.startsWith("#") || href.startsWith("?")) return null;

  let url: URL;
  try {
    // No base, on purpose: a relative href throws here, and relative means in-app.
    url = new URL(href);
  } catch {
    return null;
  }

  if (!ALLOWED_PROTOCOLS.has(url.protocol)) return null;

  if (url.protocol === "http:" || url.protocol === "https:") {
    if (!url.host) return null;

    // The app's own host is checked FIRST and unconditionally. It is redundant today — the app is
    // never served from a public marketing host — but ordering it after the allowlist would mean
    // that if the app were ever moved onto `refabric.com` or `www.refabric.com`, prose could link
    // into the very page the reader is on. Cheap to make that impossible rather than documented.
    if (isAppHost(url, appHost)) return null;

    // Anything else under refabric.com is decided here, on the canonicalised hostname, and NOT
    // delegated to the backend scrubber — its regex works on the RAW text and misses every
    // equivalent spelling of the host. Only the public marketing hosts survive.
    const hostname = stripTrailingDots(url.hostname);
    if (isRefabricHost(hostname)) return PUBLIC_REFABRIC_HOSTS.has(hostname) ? href : null;
  }

  return href;
};

/** `node` is react-markdown's AST node: it arrives in props and is not a DOM attribute. */
interface ChatbotProseLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  node?: unknown;
}

/**
 * The `a` renderer for model-authored markdown.
 *
 * A component rather than an inline arrow in `DashboardChatbot.tsx` for one reason: react-markdown
 * 8 is ESM-only and CRA's jest cannot transform it, so `DashboardChatbot.test.tsx` mocks the whole
 * library away with a passthrough. A link assertion written there would render no anchor at all and
 * pass no matter what this policy did. Testing the real component directly is the only way to prove
 * anything here; what remains unproven by that test — that react-markdown is actually handed this
 * component — is a single prop, asserted separately by reading the call site.
 *
 * A rejected href degrades to a plain `<span>`: the link text survives, so the sentence still reads
 * and nothing the model said is silently deleted. Only the click is taken away.
 */
export const ChatbotProseLink = ({
  // Destructured only to keep it OUT of `anchorProps`: react-markdown passes its AST node here and
  // it is not a DOM attribute, so spreading it would emit `node="[object Object]"` plus a warning.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  node,
  href,
  children,
  ...anchorProps
}: ChatbotProseLinkProps) => {
  const safeHref = safeChatbotLinkHref(href, window.location.host);

  if (!safeHref) return <span>{children}</span>;

  return (
    <a {...anchorProps} href={safeHref} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};
